import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useQueryParamSessionStorageState } from '../../../package/react-hooks';
import { createSafeContext } from '../../../util/createSafeContext';
import { ScheduleOfActivities } from '@faro/study-designer-model';
import { isSoAIdValid } from '../../../util/validators/selectionValidators';

export interface ScheduleOfActivitiesContextValue {
    selectedScheduleOfActivitiesId: string | undefined;
    setSelectedScheduleOfActivitiesId: (value: string | undefined) => void;
}

export const SelectedScheduleOfActivitiesContext = React.createContext({} as ScheduleOfActivitiesContextValue);

// Provides selectedScheduleOfActivitiesId and setSelectedScheduleOfActivitiesId to ScheduleOfActivities Section and Insights.
// By default the state is undefined to prevent unnecessary API calls. Only when the user visits either components that consumes
// the context, if the state is not already set (via Query Params or Storage), then the state is set to the default scheduleId.
export function ScheduleOfActivitiesProvider({ children }: PropsWithChildren<unknown>): React.JSX.Element {
    // Because we don't want to make unnecessary API calls, we don't have a list of schedules when this state is created.
    // Without the list of schedules, we can't validate the selectedScheduleOfActivitiesId, so it isn't using the useStickyLinkState hook.
    const [selectedScheduleOfActivitiesId, setSelectedScheduleOfActivitiesId] = useState<string | undefined>(undefined);

    const contextValue = useMemo(() => {
        return { selectedScheduleOfActivitiesId, setSelectedScheduleOfActivitiesId };
    }, [selectedScheduleOfActivitiesId, setSelectedScheduleOfActivitiesId]);

    return <ScheduleOfActivitiesInnerProvider value={contextValue}>{children}</ScheduleOfActivitiesInnerProvider>;
}

const [ScheduleOfActivitiesInnerProvider, useScheduleOfActivitiesContext] =
    createSafeContext<ScheduleOfActivitiesContextValue>('ScheduleOfActivities');

export { useScheduleOfActivitiesContext };

// This hook is used to set the selectedScheduleOfActivitiesId to the defaultSoAId if it is not already set.
// The state selectedScheduleOfActivitiesId is set via Query Params or Storage if those are available.
export const useSelectedSoAIdWithParamStorage = (
    defaultSoAId: string | undefined,
    schedules: ScheduleOfActivities[] | undefined
): {
    selectedSoAId: string | undefined;
    setSelectedSoAId: (value: string | undefined) => void;
    isSelectedSoAIdValid: boolean;
} => {
    const { selectedScheduleOfActivitiesId, setSelectedScheduleOfActivitiesId } = useScheduleOfActivitiesContext();

    const [selectedSoAId, setSelectedSoAId] = useQueryParamSessionStorageState('soaId', defaultSoAId, [
        selectedScheduleOfActivitiesId,
        setSelectedScheduleOfActivitiesId,
    ]);

    const isSelectedSoAIdValid = useMemo(() => {
        return isSoAIdValid(selectedSoAId, schedules);
    }, [selectedSoAId, schedules]);

    useEffect(() => {
        if (!defaultSoAId || !schedules) return;

        if (!selectedSoAId || !isSoAIdValid(selectedSoAId, schedules)) {
            setSelectedSoAId(defaultSoAId);
        }
    }, [defaultSoAId, schedules, selectedSoAId, setSelectedSoAId]);

    return {
        selectedSoAId,
        setSelectedSoAId,
        isSelectedSoAIdValid,
    };
};
